/**
 * layerTree.js — opérations pures sur l'arborescence de calques (groupes/sous-groupes).
 *
 * Modèle ADDITIF & rétro-compatible :
 *  - `elements` reste un TABLEAU PLAT ; l'ordre du tableau = z-order entre frères.
 *  - Un calque peut porter `parentId` (id du groupe parent) ; absent/null = racine.
 *  - Un groupe est un élément `type: 'group'` (conteneur sans pixels propres) avec
 *    `isExpanded` pour l'UI. Les opérations de groupe (déplacer, masquer, verrouiller,
 *    opacité) se propagent EN CASCADE à ses descendants.
 *
 * Aucune dépendance — testable isolément (Node) et réutilisable (store, panneau, canvas, export).
 */

export const isGroup = (el) => !!el && el.type === 'group';

/** Indexe les éléments par parentId (null = racine), en préservant l'ordre du tableau. */
function groupByParent(elements) {
  const m = new Map();
  for (const e of elements) {
    const p = e.parentId || null;
    if (!m.has(p)) m.set(p, []);
    m.get(p).push(e);
  }
  return m;
}

/** Enfants directs d'un parent (null = racine), dans l'ordre du tableau. */
export function childrenOf(elements, parentId) {
  const pid = parentId || null;
  return elements.filter((e) => (e.parentId || null) === pid);
}

/**
 * Aplati l'arbre dans l'ORDRE DE PEINTURE (DFS : frères dans l'ordre tableau,
 * descente dans les groupes). Renvoie [{ el, depth, effHidden, effLocked }].
 * effHidden/effLocked = valeur EFFECTIVE après cascade depuis les ancêtres.
 */
export function flattenForRender(elements) {
  const byParent = groupByParent(elements);
  const out = [];
  const walk = (pid, depth, hid, lck) => {
    for (const e of byParent.get(pid) || []) {
      const effHidden = hid || !!e.hidden;
      const effLocked = lck || !!e.locked;
      out.push({ el: e, depth, effHidden, effLocked });
      if (isGroup(e)) walk(e.id, depth + 1, effHidden, effLocked);
    }
  };
  walk(null, 0, false, false);
  return out;
}

/** Feuilles (non-groupes) dans l'ordre de peinture — ce que le canvas rend réellement. */
export function orderedLeaves(elements) {
  return flattenForRender(elements).filter((x) => !isGroup(x.el));
}

/** Tous les ids descendants d'un groupe (récursif, hors le groupe lui-même). */
export function descendantIds(elements, groupId) {
  const byParent = groupByParent(elements);
  const out = [];
  const visit = (id) => {
    for (const e of byParent.get(id) || []) {
      out.push(e.id);
      if (isGroup(e)) visit(e.id);
    }
  };
  visit(groupId);
  return out;
}

/** nodeId est-il dans le sous-arbre de ancestorId ? (garde anti-cycle pour le reparentage) */
export function isDescendant(elements, ancestorId, nodeId) {
  const byId = new Map(elements.map((e) => [e.id, e]));
  let cur = byId.get(nodeId);
  let guard = 0;
  while (cur && cur.parentId && guard++ < 1000) {
    if (cur.parentId === ancestorId) return true;
    cur = byId.get(cur.parentId);
  }
  return false;
}

/** BBox (mm) d'un groupe = union des bbox des feuilles descendantes. */
export function computeGroupBBox(elements, groupId) {
  const byId = new Map(elements.map((e) => [e.id, e]));
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const id of descendantIds(elements, groupId)) {
    const e = byId.get(id);
    if (!e || isGroup(e)) continue;
    x0 = Math.min(x0, e.x); y0 = Math.min(y0, e.y);
    x1 = Math.max(x1, e.x + (e.width || 0)); y1 = Math.max(y1, e.y + (e.height || 0));
  }
  if (x0 === Infinity) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Crée un groupe à partir d'éléments sélectionnés.
 * - On exclut les ids déjà descendants d'un autre id sélectionné (évite le double-parentage).
 * - Le nouveau groupe hérite du parent du 1er élément et se place à sa position z.
 * Renvoie un NOUVEAU tableau (immutable).
 */
export function groupSelection(elements, ids, groupNodeInput) {
  const idset = new Set(ids);
  // garde uniquement les "racines" de la sélection (pas les descendants d'autres sélectionnés)
  const topIds = ids.filter((id) => !ids.some((other) => other !== id && isDescendant(elements, other, id)));
  if (topIds.length === 0) return elements;
  const topSet = new Set(topIds);
  const firstIdx = elements.findIndex((e) => topSet.has(e.id));
  const parent = elements[firstIdx]?.parentId || null;
  const groupNode = { ...groupNodeInput, type: 'group', parentId: parent, isExpanded: true };
  const reparented = elements.map((e) => (topSet.has(e.id) ? { ...e, parentId: groupNode.id } : e));
  const arr = reparented.slice();
  arr.splice(firstIdx, 0, groupNode); // place le groupe juste avant le 1er enfant (z-order)
  return arr;
}

/** Dégroupe : remonte les enfants directs au parent du groupe puis supprime le groupe. */
export function ungroup(elements, groupId) {
  const grp = elements.find((e) => e.id === groupId);
  if (!grp || !isGroup(grp)) return elements;
  const parent = grp.parentId || null;
  return elements
    .filter((e) => e.id !== groupId)
    .map((e) => (e.parentId === groupId ? { ...e, parentId: parent } : e));
}

/** Supprime un groupe ET tout son sous-arbre. Renvoie le nouveau tableau. */
export function removeSubtree(elements, groupId) {
  const toRemove = new Set([groupId, ...descendantIds(elements, groupId)]);
  return elements.filter((e) => !toRemove.has(e.id));
}

/**
 * Déplace `dragId` par rapport à `targetId` (drag-and-drop dans le panneau).
 * `where` (sémantique panneau, le plus en AVANT en HAUT) :
 *   - 'above'  : juste au-dessus de la cible (= plus en avant, même parent)
 *   - 'below'  : juste en-dessous (= plus en arrière, même parent)
 *   - 'inside' : devient enfant de la cible (un groupe), en tête (front-most)
 * Garde anti-cycle : on ne peut pas déposer un nœud dans lui-même ni dans un de
 * ses descendants. Les descendants d'un groupe déplacé le suivent (via parentId).
 * Renvoie un NOUVEAU tableau (immuable).
 */
export function moveNode(elements, dragId, targetId, where) {
  if (!dragId || !targetId || dragId === targetId) return elements;
  const drag = elements.find((e) => e.id === dragId);
  const target = elements.find((e) => e.id === targetId);
  if (!drag || !target) return elements;

  if (where === 'inside') {
    if (isDescendant(elements, dragId, targetId)) return elements; // cycle
  } else {
    const newParent = target.parentId || null;
    if (newParent === dragId || (newParent && isDescendant(elements, dragId, newParent))) return elements;
  }

  // Retire le nœud déplacé (ses descendants restent — leur parentId est inchangé).
  const arr = elements.filter((e) => e.id !== dragId);

  let newParentId, insertIdx;
  if (where === 'inside') {
    newParentId = targetId;
    const ti = arr.findIndex((e) => e.id === targetId);
    let lastChild = ti;
    for (let k = 0; k < arr.length; k++) {
      if ((arr[k].parentId || null) === targetId) lastChild = k;
    }
    insertIdx = Math.max(ti, lastChild) + 1; // en tête de groupe (front-most)
  } else {
    const ti = arr.findIndex((e) => e.id === targetId);
    newParentId = arr[ti].parentId || null;
    insertIdx = where === 'above' ? ti + 1 : ti; // 'above' panneau = plus tard dans le tableau
  }

  arr.splice(insertIdx, 0, { ...drag, parentId: newParentId || null });
  return arr;
}

/** Champs par défaut d'un élément (alignés sur store.addElement) — pour compléter
 *  les éléments importés (formes vectorielles, images…) afin que l'inspecteur et
 *  le rendu disposent de toutes les propriétés attendues. */
export const ELEMENT_BASE_DEFAULTS = {
  locked: false, hidden: false, layerColor: 'transparent',
  fillType: 'solid',
  gradient: { type: 'linear', angle: 90, stops: [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }] },
  strokeEnabled: false, strokeType: 'solid', strokeWidth: 2, strokeColor: '#FFFFFF',
  strokeGradient: { type: 'linear', angle: 90, stops: [{ color: '#FFFFFF', position: 0 }, { color: '#000000', position: 100 }] },
  shadowEnabled: false, shadowColor: '#000000', shadowOpacity: 75, shadowAngle: 120,
  shadowDistance: 5, shadowSpread: 0, shadowSize: 5, shadowInset: false,
};

/**
 * Remappe les ids PROVISOIRES d'un arbre importé ('v1','v2'…) vers des ids
 * uniques, en PRÉSERVANT la hiérarchie (parentId pointe vers le bon nouvel id).
 * Complète chaque élément avec ELEMENT_BASE_DEFAULTS (objets imbriqués clonés).
 */
export function remapTreeIds(elements) {
  const idMap = new Map();
  let seq = 0;
  const stamp = (typeof Date !== 'undefined' && Date.now) ? Date.now() : 0;
  const fresh = (old) => {
    if (old == null) return null;
    if (!idMap.has(old)) idMap.set(old, `el_${stamp}_${seq++}${Math.random().toString(36).slice(2, 6)}`);
    return idMap.get(old);
  };
  // id propre : toujours frais (même si l'élément source n'a pas d'id, ex. calques
  // texte de pdf_extract) — sinon plusieurs éléments id-less collisionneraient sur null.
  const freshOwn = (old) => (old != null ? fresh(old) : `el_${stamp}_o${seq++}${Math.random().toString(36).slice(2, 6)}`);
  return (elements || []).map((e) => ({
    ...JSON.parse(JSON.stringify(ELEMENT_BASE_DEFAULTS)),
    ...e,
    id: freshOwn(e.id),
    parentId: e.parentId != null ? fresh(e.parentId) : null,
  }));
}

/** Profondeur d'imbrication d'un élément (0 = racine). */
export function depthOf(elements, id) {
  const byId = new Map(elements.map((e) => [e.id, e]));
  let cur = byId.get(id), d = 0, guard = 0;
  while (cur && cur.parentId && guard++ < 1000) { d++; cur = byId.get(cur.parentId); }
  return d;
}
